import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CalendarView = ({ actividades, meses }) => {
  const [mesActual, setMesActual] = useState(new Date().getMonth());
  const [anioActual, setAnioActual] = useState(new Date().getFullYear());

  const obtenerDiasDelMes = (mes, anio) => {
    return new Date(anio, mes + 1, 0).getDate();
  };

  const obtenerPrimerDia = (mes, anio) => {
    return new Date(anio, mes, 1).getDay();
  };

  const actividadesPorFecha = {};
  actividades.forEach(a => {
    const fecha = a.fechaInicio;
    if (!actividadesPorFecha[fecha]) {
      actividadesPorFecha[fecha] = [];
    }
    actividadesPorFecha[fecha].push(a);
  });

  const obtenerColorEstado = (estado) => {
    switch(estado) {
      case 'sin iniciar': return 'bg-red-500';
      case 'en proceso': return 'bg-yellow-500';
      case 'finalizada': return 'bg-green-500';
      case 'cancelada': return 'bg-gray-500';
      case 'programada': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const irMesAnterior = () => {
    if (mesActual === 0) {
      setMesActual(11);
      setAnioActual(anioActual - 1);
    } else {
      setMesActual(mesActual - 1);
    }
  };

  const irMesSiguiente = () => {
    if (mesActual === 11) {
      setMesActual(0);
      setAnioActual(anioActual + 1);
    } else {
      setMesActual(mesActual + 1);
    }
  };

  const diasDelMes = obtenerDiasDelMes(mesActual, anioActual);
  const primerDia = obtenerPrimerDia(mesActual, anioActual);
  const dias = [];

  // Dias anteriores vacios
  for (let i = 0; i < primerDia; i++) {
    dias.push(null);
  }

  // Dias del mes
  for (let i = 1; i <= diasDelMes; i++) {
    dias.push(i);
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header del Calendario */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={irMesAnterior}
          className="p-2 hover:bg-gray-200 rounded-lg transition"
        >
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-2xl font-bold">
          {meses[mesActual]} {anioActual}
        </h2>
        <button
          onClick={irMesSiguiente}
          className="p-2 hover:bg-gray-200 rounded-lg transition"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Dias de la semana */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sab', 'Dom'].map(dia => (
          <div key={dia} className="text-center font-bold text-gray-600 py-2">
            {dia}
          </div>
        ))}
      </div>

      {/* Dias del calendario */}
      <div className="grid grid-cols-7 gap-2">
        {dias.map((dia, index) => {
          const fecha = dia ? `${anioActual}-${String(mesActual + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}` : null;
          const actividadesDelDia = fecha ? (actividadesPorFecha[fecha] || []) : [];

          return (
            <div
              key={index}
              className={`min-h-24 p-2 rounded-lg border-2 ${
                dia ? 'border-gray-200 bg-gray-50 hover:bg-gray-100' : 'bg-gray-100 border-gray-100'
              } transition cursor-pointer overflow-hidden`}
            >
              {dia && (
                <>
                  <div className="font-bold text-gray-800 mb-1">{dia}</div>
                  <div className="space-y-1">
                    {actividadesDelDia.slice(0, 2).map(actividad => (
                      <div
                        key={actividad.id}
                        className={`text-xs text-white px-2 py-1 rounded truncate ${obtenerColorEstado(actividad.estado)}`}
                        title={actividad.nombre}
                      >
                        {actividad.nombre}
                      </div>
                    ))}
                    {actividadesDelDia.length > 2 && (
                      <div className="text-xs text-gray-600 px-2">
                        +{actividadesDelDia.length - 2} más
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="font-bold mb-3">Leyenda de Estados:</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-sm">Sin iniciar</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-sm">En proceso</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-sm">Finalizada</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-500 rounded"></div>
            <span className="text-sm">Cancelada</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-sm">Programada</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
